/* @v0.1 - ANUBIS */
import axios from 'axios';
import { apiUrl } from '../helpers/utils';
import { IChildRegistration, ILogin, IMailUpdateVerification, IPasswordUpdate, IRegistration, IUser } from '../data/models/user/IUser';
import { FileAttachment } from '../components/common/AttachmentPreview';
import { INeuroSkillFileManifest } from '../data/models/neuro/INeuroSkill';
import { IWhatsAppFlow } from '../data/models/IWhatsApp';
import { ITopTokenUsersResponse } from '../data/models/neuro/INeuroSkillUsage';

/*
const getHatateToken = () => {
    const token = localStorage.getItem('hatateToken');
    return token ? `Bearer ${token}` : '';
  };
*/
const getHatateToken = () => {
  const token = localStorage.getItem('hatateToken');
  const tokenExpiration = localStorage.getItem('hatateTokenExpiration');

  // Verificar si el token existe y no ha expirado
  if (token) {
    if (!tokenExpiration || parseInt(tokenExpiration) > Date.now()) {
      return `Bearer ${token}`;
    }
    
    // Token expirado, limpiar
    localStorage.removeItem('hatateToken');
    localStorage.removeItem('hatateTokenExpiration');
  }

  return '';
};

const getErrorMessage = (status: number, statusText: string): string => {
  switch (status) {
    case 401:
      return 'Error de autenticación: Token inválido o expirado';
    case 403:
      return 'Acceso denegado: No tienes permisos para realizar esta acción, por favor refresca la página.';
    case 404:
      return 'Recurso no encontrado';
    case 429:
      return 'Demasiadas peticiones: Intenta de nuevo más tarde';
    case 500:
      return 'Error interno del servidor';
    case 502:
      return 'Error de conexión con el servidor';
    case 503:
      return 'Servicio no disponible temporalmente';
    case 504:
      return 'Tiempo de espera agotado';
    default:
      return `Error del servidor (${status}): ${statusText}`;
  }
};

export const UserService = {
  /*register: async (registration: IRegistration): Promise<string> => {
    const { confirmPassword, ...registrationData } = registration;
    const headers = {
      'Content-Type': 'application/json',
    };
    try {
      const response = await axios.post(`${apiUrl}authentication/register`, registrationData, { headers });

      localStorage.setItem("hatateToken", response.data.token);
      
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data.message : error.message;
    }
  },*/

  getStoredToken: () => {
    const token = getHatateToken();
    return token;
  },

  register: async (registration: IRegistration): Promise<string> => {
    const { confirmPassword, ...registrationData } = registration;
    const headers = {
      'Content-Type': 'application/json',
    };
    try {
      const response = await axios.post(`${apiUrl}authentication/register`, registrationData, { headers });
  
      // Guardar token
      localStorage.setItem("hatateToken", response.data.token);
      
      // Guardar tiempo de expiración si está disponible en la respuesta
      if (response.data.expiresAt) {
        localStorage.setItem('hatateTokenExpiration', response.data.expiresAt);
      } else {
        // Si no hay expiración explícita, establecer una por defecto (por ejemplo, 1 hora)
        localStorage.setItem('hatateTokenExpiration', (Date.now() + 3600000).toString());
      }
  
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  registerChild: async (childRegistration: IChildRegistration): Promise<string> => {
    const { confirmPassword, ...registrationData } = childRegistration;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      const response = await axios.post(`${apiUrl}authentication/register-child`, registrationData, { headers });
  
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  /*login: async (login: ILogin): Promise<string> => {
    const jsonParams = JSON.stringify(login);

    const headers = { 'Content-Type': 'application/json' };
    try {
      const response = await axios.post(`${apiUrl}authentication/login`, jsonParams, { headers });

      localStorage.setItem("hatateToken", response.data.token);

      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  }, */
  login: async (login: ILogin): Promise<string> => {
    const jsonParams = JSON.stringify(login);
  
    const headers = { 'Content-Type': 'application/json' };
    try {
      const response = await axios.post(`${apiUrl}authentication/login`, jsonParams, { headers });
  
      // Guardar token
      localStorage.setItem("hatateToken", response.data.token);
      
      // Guardar tiempo de expiración si está disponible en la respuesta
      if (response.data.expiresAt) {
        localStorage.setItem('hatateTokenExpiration', response.data.expiresAt);
      } else {
        // Si no hay expiración explícita, establecer una por defecto (por ejemplo, 1 hora)
        localStorage.setItem('hatateTokenExpiration', (Date.now() + 3600000 * 24).toString());
      }
  
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  /*logout: async () => {
    localStorage.removeItem('hatateToken');
  },*/
  logout: async () => {
    localStorage.removeItem('hatateToken');
    localStorage.removeItem('hatateTokenExpiration');
    //return new Promise(resolve => setTimeout(resolve, 100));
  },
  
  getUser: async (id?: string) : Promise<IUser> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    const url = id ? `${apiUrl}/user/${id}` : `${apiUrl}user/profile/me`;
    try {
      const response = await axios.get(url, { headers });

      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  getUserParent: async (id?: string) : Promise<IUser | null> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };

    const url = id ? `${apiUrl}/user/${id}` : `${apiUrl}user/profile/me`;

    try {
      const response = await axios.get(url, { headers });

      return response.data.parent;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  getUserChildren: async (id?: string) : Promise<IUser[] | null> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };

    const url = id ? `${apiUrl}/user/${id}` : `${apiUrl}user/profile/me`;

    try {
      const response = await axios.get(url, { headers });

      return response.data.children;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  getChatTokensRemaining: async (id?: string) : Promise<Number> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    const url = id ? `${apiUrl}/user/${id}` : `${apiUrl}user/profile/me`;
    try {
      const response = await axios.get(url, { headers });

      const userData : IUser = response.data;

      const tokenUsed = userData.botInfo.tokenUsed;
      const tokenLimit = userData.botInfo.tokenLimit;

      return tokenLimit - tokenUsed;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  getChatTokensUsed: async (id?: string) : Promise<Number> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    const url = id ? `${apiUrl}/user/${id}` : `${apiUrl}user/profile/me`;
    try {
      const response = await axios.get(url, { headers });

      const userData : IUser = response.data;

      const tokenUsed = userData.botInfo.tokenUsed; // Tokens usados
      
      return tokenUsed;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  getChatTokensLimit: async (id?: string) : Promise<Number> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    const url = id ? `${apiUrl}/user/${id}` : `${apiUrl}user/profile/me`;
    try {
      const response = await axios.get(url, { headers });

      const userData : IUser = response.data;

      const tokenUsed = userData.botInfo.tokenLimit; // limit token
      
      return tokenUsed;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  getAvailableNeuroSkills: async () => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    const url = `${apiUrl}/bot/neuroskill/list`;
    try {
      const response = await axios.get(url, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  getTopNeuroSkillUsage: async (): Promise<ITopTokenUsersResponse> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };

    const url = `${apiUrl}/neuro-usage/top-users`;
    try {
      const response = await axios.get(url, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  /** 
   * whatsapp stuff
  */

  // Existing method for getting WhatsApp flows
  getWhatsAppFlows: async (id?: string): Promise<IWhatsAppFlow[]> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    const url = id ? `${apiUrl}/user/${id}` : `${apiUrl}user/profile/me`;
    try {
      const response = await axios.get(url, { headers });
      return response.data.whatsappInfo.flows;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },
  
  // Get all WhatsApp flows directly from the WhatsApp endpoint
  getAllWhatsAppFlows: async (): Promise<IWhatsAppFlow[]> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      const response = await axios.get(`${apiUrl}whatsapp/flows`, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },
  
  // Get a specific WhatsApp flow by ID
  getWhatsAppFlowById: async (flowId: string): Promise<IWhatsAppFlow> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      const response = await axios.get(`${apiUrl}whatsapp/flows/${flowId}`, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },
  
  // Create a new WhatsApp flow
  createWhatsAppFlow: async (flow: Omit<IWhatsAppFlow, '_id'>): Promise<IWhatsAppFlow> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      const response = await axios.post(`${apiUrl}whatsapp/flows`, flow, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },
  
  // Update an existing WhatsApp flow
  updateWhatsAppFlow: async (flow: IWhatsAppFlow): Promise<IWhatsAppFlow> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      const response = await axios.put(`${apiUrl}whatsapp/flows/${flow._id}`, flow, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },
  
  // Delete a WhatsApp flow
  deleteWhatsAppFlow: async (flowId: string): Promise<void> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      await axios.delete(`${apiUrl}whatsapp/flows/${flowId}`, { headers });
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  getPromptResponse: async (
    chatId: string,
    neuroSkill: string,
    neuroModule: string,
    messages: { role: 'user' | 'assistant'; content: string; time: string; }[],
    fileAttachment?: FileAttachment
  ): Promise<string> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
  
    const url = apiUrl + "bot/prompt";
  
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          chatId: chatId,
          skill: neuroSkill,
          module: neuroModule,
          prompt: messages,
          raw: fileAttachment?.raw,
        }),
      });
  
      if (!response.ok) {
        const errorMessage = await response.json();
        throw new Error(`Error ${response.status}: ${errorMessage.message}`);
      }
  
      const data = await response.json();
      if (typeof data.reply !== 'string') {
        throw new Error('Unexpected response format: reply is not a string');
      }
      
      return data.reply;
    } catch (error: any) {
      return error.message;
    }
  },

  getAnalyzeResponse: async (
    text: string
  ): Promise<string> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
  
    const url = apiUrl + "bot/analyze";
  
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          prompt: text,
        }),
      });
  
      if (!response.ok) {
        const errorMessage = await response.json();
        throw new Error(`Error ${response.status}: ${errorMessage.message}`);
      }
  
      const data = await response.json();
      if (typeof data.reply !== 'string') {
        throw new Error('Unexpected response format: reply is not a string');
      }
      
      return data.reply;
    } catch (error: any) {
      return error.message;
    }
  },


  getStreamResponse: async (
    chatId: string,
    neuroSkill: string,
    neuroModule: string,
    messages: { role: 'user' | 'assistant'; content: string; time: string, attachments?: { images?: string[]; file?: FileAttachment } }[],
    onMessage: (message: string) => void,
    onError?: (error: string) => void,
    onComplete?: () => void,
    fileAttachment?: string,
    imageAttachment?: string,
  ): Promise<void> => {
    
    // Función helper para hacer el stream request
    const makeStreamRequest = async (url: string, payload: any): Promise<Response> => {
      return await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getHatateToken(),
        },
        body: JSON.stringify(payload)
      });
    };

    // Función helper para procesar el stream
    const processStream = async (response: Response): Promise<void> => {
      if (!response.body) {
        throw new Error('No se pudo obtener el cuerpo del stream.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      
      let buffer = '';
      let hasCompleted = false;
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            if (!hasCompleted && onComplete) {
              hasCompleted = true;
              onComplete();
            }
            break;
          }
          
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          
          for (const line of lines) {
            if (line.startsWith('data:')) {
              const cleanLine = line.slice(5).trim();
              
              if (cleanLine) {
                try {
                  const data = JSON.parse(cleanLine);
                  
                  if (data.done) {
                    if (!hasCompleted && onComplete) {
                      hasCompleted = true;
                      onComplete();
                    }
                    return;
                  } else if (data.error) {
                    throw new Error(data.error);
                  } else if (data.content) {
                    if (onMessage) {
                      onMessage(data.content);
                    }
                  }
                } catch (parseError) {
                  console.error('Error al parsear JSON del stream:', cleanLine, parseError);
                  throw parseError;
                }
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    };

    // URLs para ambos endpoints
    const primaryUrl = apiUrl + 'bot/stream';
    const fallbackUrl = apiUrl + 'bot/stream-openrouter';
    
    // Payload para el endpoint principal
    const primaryPayload = {
      chatId: chatId,
      skill: neuroSkill,
      module: neuroModule,
      prompt: messages,
      raw: imageAttachment
    };
    
    // Payload para el endpoint de fallback (OpenRouter)
    const fallbackPayload = {
      chatId: chatId,
      skill: neuroSkill, // OpenRouter lo marca como opcional, pero lo enviamos
      module: neuroModule, // OpenRouter lo marca como opcional, pero lo enviamos
      prompt: messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        time: msg.time
        // Note: OpenRouter no acepta attachments según el schema
      }))
    };

    try {
      // Intento 1: Endpoint principal
      console.log('Intentando con endpoint principal...');
      
      try {
        const response = await makeStreamRequest(primaryUrl, primaryPayload);
        
        if (!response.ok) {
          throw new Error(getErrorMessage(response.status, response.statusText));
        }
        
        // Si la respuesta es exitosa, procesar el stream
        await processStream(response);
        return; // Éxito con el endpoint principal
        
      } catch (primaryError) {
        console.warn('Error en endpoint principal:', primaryError);
        
        // Intento 2: Fallback a OpenRouter
        console.log('Intentando con fallback (OpenRouter)...');
        
        try {
          const fallbackResponse = await makeStreamRequest(fallbackUrl, fallbackPayload);
          
          if (!fallbackResponse.ok) {
            throw new Error(getErrorMessage(fallbackResponse.status, fallbackResponse.statusText));
          }
          
          // Si el fallback es exitoso, procesar el stream
          await processStream(fallbackResponse);
          return; // Éxito con el fallback
          
        } catch (fallbackError) {
          console.error('Error en fallback (OpenRouter):', fallbackError);
          
          // Ambos endpoints fallaron
          const combinedError = `Endpoint principal falló: ${primaryError.message}. Fallback (OpenRouter) falló: ${fallbackError.message}`;
          
          if (onError) {
            onError(combinedError);
          }
          return;
        }
      }
      
    } catch (error: any) {
      console.error('Error general en getStreamResponse:', error);
      
      let errorMessage = 'Error en la conexión de streaming';
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Error de red - no se pudo conectar al servidor';
      } else {
        errorMessage = `Error en la petición: ${error.message}`;
      }
      
      if (onError) {
        onError(errorMessage);
      }
    }
  },

  getChatSessionById: async (chatId: string) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    const url = `${apiUrl}/bot/chatsession/${chatId}`;
    try {
      const response = await axios.get(url, { headers });
      return response.data;
    } catch (error: any) {
      console.error('Error in getChatSessionById:', error);
      throw error.response ? error.response.data : error.message;
    }
  },

  getChatSessionsPaginated: async (limit: number = 10, page: number = 1) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };

    const url = `${apiUrl}/bot/chatsessions?limit=${limit}&page=${page}`;
    try {
      const response = await axios.get(url, { headers });
      return response.data; 
    } catch (error: any) {
      console.error('Error in getChatSessionsPaginated:', error);
      throw error.response ? error.response.data : error.message;
    }
  },

  mailVerification: async () => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      const response = await axios.get(`${apiUrl}user/update-mail-verification`, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  mailUpdate: async (update: IMailUpdateVerification) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      const response = await axios.post(`${apiUrl}user/update-mail-verification`, update, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  updateUser: async (user: IUser) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      const response = await axios.put(`${apiUrl}user`, user, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  passwordUpdate: async (request: IPasswordUpdate) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    try {
      const response = await axios.put(`${apiUrl}user/update-password`, request, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  userListAutocompleteObservable: async (own = false, search = '') => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };
    const route = own ? '/true' : '';
    const searchQuery = search ? `?search=${search}` : '';
    try {
      const response = await axios.get(`${apiUrl}user/actions/list-all${route}${searchQuery}`, { headers });
      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  },

  userIp: async () => {
    try {
      const response = await axios.get('https://api.ipify.org?format=json');
      return response.data;
    } catch (error: any) {
      console.log(error)
      throw error.response ? error.response.data : error.message;
    }
  },

  requestNeuroSkillFileManifest: async (file: File): Promise<INeuroSkillFileManifest> => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: getHatateToken(),
    };

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${apiUrl}files/request-neuro-manifest`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });

      return response.data;
    } catch (error: any) {
      throw error.response ? error.response.data : error.message;
    }
  }
}